import {
  ComponentPropsWithRef,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  ReactNode,
  useState,
  useMemo,
  FC,
} from 'react';
import { DateRange as DateRangeType, DayPickerProps, PropsRange } from 'react-day-picker';
import { VariantProps, cva } from 'class-variance-authority';
import { DateTime } from 'luxon';

import { PopoverContent, PopoverTrigger, Popover } from '../popover/popover';
import { Calendar as CalendarIcon } from '../icons';
import { DateFormatted } from '../date/date';
import { Button } from '../button/button';
import { Calendar } from './calendar';
import { cn } from '../../utils';

const compareDateRanges = (date1: DateRangeType | undefined, date2: DateRangeType | undefined) => {
  if (!date1?.from || !date1?.to || !date2?.from || !date2?.to) return false;
  return (
    DateTime.fromJSDate(date1.from).equals(DateTime.fromJSDate(date2.from)) &&
    DateTime.fromJSDate(date1.to).equals(DateTime.fromJSDate(date2.to))
  );
};

const isSameDay = (date1: DateTime, date2: DateTime) => {
  return date1.hasSame(date2, 'day');
};

const isToday = (date: DateTime) => {
  return isSameDay(date, DateTime.now());
};

const isYesterday = (date: DateTime) => {
  return isSameDay(date, DateTime.now().minus({ days: 1 }));
};

const isTodaySpan = (from: DateTime, to: DateTime) => {
  return isToday(from) && isToday(to);
};

const isYesterdaySpan = (from: DateTime, to: DateTime) => {
  return isYesterday(from) && isYesterday(to);
};

const isMonthSpan = (from: DateTime, to: DateTime) => {
  return (
    from.hasSame(to, 'month') &&
    isSameDay(from, from.startOf('month')) &&
    isSameDay(to, to.endOf('month'))
  );
};

const isYearSpan = (from: DateTime, to: DateTime) => {
  return (
    from.hasSame(to, 'year') &&
    isSameDay(from, from.startOf('year')) &&
    isSameDay(to, to.endOf('year'))
  );
};

export const dateRangeVariants = cva('min-w-40 justify-start text-left font-normal', {
  variants: {
    isSelected: {
      true: 'text-content',
      false: 'text-weak',
    },
    hasShadow: { true: 'shadow-sm disabled:shadow-none' },
  },
  defaultVariants: {
    hasShadow: false,
    isSelected: false,
  },
});

export type DateRangeProps = {
  autoRangeOptions?: { value: DateRangeType; display?: string; label: string }[];
  translations: {
    yesterday: string;
    today: string;
  };
  placeholder: string;
  required?: boolean;
  lang: string;
} & Omit<Partial<DayPickerProps> & Partial<PropsRange>, 'required'> &
  Omit<VariantProps<typeof dateRangeVariants>, 'isSelected'> &
  PropsWithChildren;

const DateRangeContext = createContext<{
  selected: DateRangeProps['selected'];
  onSelect: DateRangeProps['onSelect'];
  close: () => void;
}>({
  selected: undefined,
  onSelect: () => {},
  close: () => {},
});

const DateRangeValue: FC<{
  translations: DateRangeProps['translations'];
  selected: DateRangeProps['selected'];
  placeholder: ReactNode;
  lang: string;
}> = ({ selected, translations, placeholder, lang }) => {
  if (!selected?.from || !selected?.to) return placeholder;

  const from = DateTime.fromJSDate(selected?.from);
  const to = DateTime.fromJSDate(selected?.to);

  if (isTodaySpan(from, to))
    return (
      <span>
        {translations.today} (
        <DateFormatted date={from.toJSDate()} format="DATE_SHORT" language={lang} />)
      </span>
    );

  if (isYesterdaySpan(from, to))
    return (
      <span>
        {translations.yesterday} (
        <DateFormatted date={from.toJSDate()} format="DATE_SHORT" language={lang} />)
      </span>
    );

  if (isMonthSpan(from, to))
    return (
      <span>
        <DateFormatted
          format={{ month: 'long', year: 'numeric' }}
          date={from.toJSDate()}
          language={lang}
        />
      </span>
    );

  if (isYearSpan(from, to))
    return (
      <span>
        <DateFormatted format={{ year: 'numeric' }} date={from.toJSDate()} language={lang} />
      </span>
    );

  return (
    <span>
      <DateFormatted date={from.toJSDate()} format="DATE_SHORT" language={lang} /> -{' '}
      <DateFormatted date={to.toJSDate()} format="DATE_SHORT" language={lang} />
    </span>
  );
};

function DateRange({
  required = false,
  autoRangeOptions,
  translations,
  lang = 'pl',
  placeholder,
  className,
  hasShadow,
  ...rest
}: DateRangeProps) {
  const { selected, onSelect } = rest;
  const [open, setOpen] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          className={cn(dateRangeVariants({ isSelected: !!selected, hasShadow }), className)}
          variant="secondary"
        >
          <CalendarIcon className="w-4 h-4" />
          <DateRangeValue
            placeholder={
              <span
                className={cn({
                  "after:content-['*'] after:text-critical": required,
                })}
              >
                {placeholder}
              </span>
            }
            translations={translations}
            selected={selected}
            lang={lang}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto grid grid-cols-3">
        <DateRangeContext.Provider value={{ selected, onSelect, close }}>
          {autoRangeOptions && (
            <div className="flex flex-col p-4 border-r border-border-default">
              {autoRangeOptions.map((option) => (
                <DateRangeQuickSelectItem value={option.value} key={option.label}>
                  {option.label}
                </DateRangeQuickSelectItem>
              ))}
            </div>
          )}
        </DateRangeContext.Provider>
        <div className={cn('relative', !autoRangeOptions ? 'col-span-3' : 'col-span-2')}>
          <Calendar showOutsideDays={false} numberOfMonths={2} mode="range" lang={lang} {...rest} />
        </div>
      </PopoverContent>
    </Popover>
  );
}

DateRange.displayName = 'DateRange';

const DateRangeQuickSelectItem: FC<ComponentPropsWithRef<'div'> & { value: DateRangeType }> = ({
  children,
  value,
  ...props
}) => {
  const { selected, onSelect, close } = useContext(DateRangeContext);

  const isSelected = useMemo(() => {
    return compareDateRanges(selected, value);
  }, [selected, value]);

  return (
    <div
      className={cn(
        'relative min-h-9 flex w-full text-weak cursor-pointer select-none items-center rounded-sm py-1.5 pl-3 pr-2 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[state=checked]:bg-surface-weak',
        props.className,
        {
          'bg-surface text-contrast': isSelected,
          'hover:bg-surface-hover': !isSelected,
        },
      )}
      {...props}
      onClick={(e) => {
        onSelect?.(value, new Date(), {}, e);
        close?.();
      }}
    >
      {children}
    </div>
  );
};

export { type DateRangeType, DateRange };
